import useSWR from 'swr';
import { useParams } from 'react-router-dom';
import { getFetcher, putFetcher } from '@/api/fetchers';
import { createFormContext, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { Button, rem, SimpleGrid } from '@mantine/core';
import { PersonalInfoCard } from './Cards/PersonalInfoCard';
import { AddressCard } from './Cards/AddressCard';
import { ContactCard } from './Cards/ContactCard';
import { FamilyContactCard } from './Cards/FamilyContactCard';
import {
  employeeDetailedSchema,
  type EmployeeDetailed,
} from '@/schemas/API/GET/employee/response';
import { type UpdateEmployee } from '@/schemas/API/PUT/employee/request';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import { displayErrorOnMantineForm } from '@/utils/error/errorHandler';
import {
  goodNotification,
  okNotification,
} from '@/components/Notification/Notification';
import { isEmptyForm } from '@/utils/functions';
import { notifications } from '@mantine/notifications';
import { IconX } from '@tabler/icons-react';

export const [EmployeeFormProvider, useEmployeeFormContext, useEmployeeForm] =
  createFormContext<EmployeeDetailed>();

export const EmployeePersonalSection: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { data: employee, mutate } = useSWR<EmployeeDetailed>(
    `/employees/${employeeId}`,
    getFetcher
  );
  const employeeForm = useEmployeeForm({
    validate: zodResolver(employeeDetailedSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (!employee) return;
    employeeForm.setInitialValues(employee);
    if (isEmptyForm(employeeForm)) employeeForm.reset();
  }, [employee]);

  function fillAttributes(): UpdateEmployee {
    const tagIDs: number[] = employeeForm.values.tags.map(tag => tag.id);
    const lockIDs: number[] = employeeForm.values.locs.map(
      location => location.id
    );
    const result: UpdateEmployee = {
      ...employeeForm.values,
      tags: tagIDs,
      locs: lockIDs,
    };
    return result;
  }

  const updateDetails = async (): Promise<void> => {
    const validationResult = employeeForm.validate();
    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: 'Formulář se nepodařilo uložit.',
        message: 'Formulář obsahuje chyby.',
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }
    const updateEmployee = fillAttributes();

    try {
      await putFetcher(`employees/${employeeId}`, updateEmployee);
      await mutate();
      employeeForm.reset();
      goodNotification('Změny byly uloženy.');
    } catch (err) {
      displayErrorOnMantineForm(err, employeeForm.setFieldError);
    }
  };

  function handleCancel(): void {
    employeeForm.reset();
    okNotification('Storno...', 'Osobní údaje jsou teď v původním stavu!');
  }

  return (
    <EmployeeFormProvider form={employeeForm}>
      <SimpleGrid cols={2}>
        <PersonalInfoCard />
        <AddressCard />
      </SimpleGrid>
      <SimpleGrid cols={2}>
        <ContactCard />
        <FamilyContactCard />
      </SimpleGrid>
      <ButtonBar>
        <Button
          variant="outline"
          radius="md"
          size="md"
          className="text-lg"
          onClick={handleCancel}
        >
          Storno
        </Button>
        <Button
          radius="md"
          size="md"
          className="text-lg"
          onClick={updateDetails}
        >
          Uložit
        </Button>
      </ButtonBar>
    </EmployeeFormProvider>
  );
};
